import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import api from "../api/axiosInstance";
import {
  fetchCandidateById,
  uploadMasterResume,
  clearCandidateError,
  clearSelectedCandidate,
} from "../slice/candidateSlice";
import { useDialog } from "../components/ConfirmDialog";
import { addedByLabel, formatDateTime } from "../utils/formatters";
import { DownloadIcon } from "../components/icons";

const TYPE_LABELS = {
  MASTER: "Master Resume",
  OPTIMIZED: "Optimized Draft",
  FINAL: "Final Resume",
};

export default function CandidateDetail() {
  const { id } = useParams();
  const [newResumeFile, setNewResumeFile] = useState(null);
  // Shown right after a new master resume is uploaded, to remind the
  // recruiter that filling the profile from it happens on the Edit page.
  const [showFillHint, setShowFillHint] = useState(false);
  const fileInputRef = useRef(null);

  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { selectedCandidate, resumeHistory, status, error } = useSelector((state) => state.candidates);
  const loading = status === "loading";
  const { dialogElement, notify } = useDialog();

  useEffect(() => {
    dispatch(fetchCandidateById(id));
    return () => {
      dispatch(clearSelectedCandidate());
      dispatch(clearCandidateError());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const isOwner = (() => {
    if (!selectedCandidate) return false;
    const ownerId =
      typeof selectedCandidate.createdBy === "object"
        ? selectedCandidate.createdBy?._id
        : selectedCandidate.createdBy;
    const currentUserId = user?.id || user?._id;
    return ownerId === currentUserId;
  })();

  const handleDownload = async (versionId, fileName) => {
    try {
      const res = await api.get(`/candidate/${id}/resume-versions/${versionId}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "resume");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      await notify("Could not download this resume version.", { title: "Download failed" });
    }
  };

  const handleUploadNewVersion = async (e) => {
    e.preventDefault();
    if (!newResumeFile) return;

    const result = await dispatch(uploadMasterResume({ id, file: newResumeFile }));
    if (uploadMasterResume.fulfilled.match(result)) {
      setNewResumeFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setShowFillHint(true);
    }
  };

  if (loading && !selectedCandidate) {
    return (
      <div className="page-container">
        <p>Loading candidate...</p>
      </div>
    );
  }

  if (!selectedCandidate) {
    return (
      <div className="page-container">
        {error && <div className="auth-error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>{selectedCandidate.name}</h2>
        {isOwner && (
          <Link to={`/dashboard/candidates/${id}/edit`}>
            <button type="button">Edit Candidate Details</button>
          </Link>
        )}
      </div>

      {error && <div className="auth-error">{error}</div>}

      <table className="simple-table">
        <tbody>
          <tr>
            <th style={{ width: "220px" }}>Email</th>
            <td>{selectedCandidate.email}</td>
          </tr>
          <tr>
            <th>Phone</th>
            <td>{selectedCandidate.phone || "-"}</td>
          </tr>
          <tr>
            <th>Added On</th>
            <td>{formatDateTime(selectedCandidate.createdAt)}</td>
          </tr>
          {user?.role === "ADMIN" && (
            <tr>
              <th>Added By</th>
              <td>{addedByLabel(selectedCandidate.createdBy)}</td>
            </tr>
          )}
        </tbody>
      </table>
      <p style={{ color: "#5a5a5a", fontSize: "13px", marginTop: "4px" }}>
        Additional details (title, company, experience, work authorization, skills, education,
        certifications) can be added on the Edit page, either typed in or filled automatically from
        the latest master resume. They improve ATS scoring accuracy but aren't required.
      </p>

      <div className="page-header" style={{ marginTop: "28px" }}>
        <h3>Resume History</h3> <br/><br/>
      </div>
      <p style={{ color: "#5a5a5a", fontSize: "14px", marginTop: "-8px" }}>
        Every resume ever uploaded or generated for this candidate, newest first.
        Uploading a new master resume never replaces an old one — it just adds
        the next version.
      </p>

      {isOwner && (
        <form onSubmit={handleUploadNewVersion} style={{ margin: "12px 0 20px" }}>
          <input
            type="file"
            ref={fileInputRef}
            accept=".docx"
            onChange={(e) => setNewResumeFile(e.target.files[0] || null)}
          />{" "}
          <button type="submit" disabled={!newResumeFile || loading}>
            {loading ? "Uploading..." : "Upload New Master Resume"}
          </button>
        </form>
      )}

      {isOwner && showFillHint && (
        <div
          style={{
            margin: "0 0 20px",
            padding: "10px 12px",
            backgroundColor: "#eaf4ec",
            border: "1px solid #b9dcc1",
            borderRadius: "4px",
            fontSize: "13px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
          }}
        >
          <span>
            New master resume uploaded. Go to{" "}
            <Link to={`/dashboard/candidates/${id}/edit`}>Edit Candidate Details</Link> to fill the
            profile from it and review the fields.
          </span>
          <button type="button" onClick={() => setShowFillHint(false)} title="Dismiss">
            ✕
          </button>
        </div>
      )}

      {resumeHistory.length === 0 ? (
        <p>No resumes uploaded yet for this candidate.</p>
      ) : (
        <table className="simple-table">
          <thead>
            <tr>
              <th>Version</th>
              <th>Type</th>
              <th>File Name</th>
              <th>ATS</th>
              {user?.role === "ADMIN" && <th>Uploaded By</th>}
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {resumeHistory.map((rv) => (
              <tr key={rv.id}>
                <td>
                  v{rv.version}
                  {rv.isLatestMaster && (
                    <span
                      style={{
                        marginLeft: "8px",
                        fontSize: "11px",
                        fontWeight: "bold",
                        color: "#fff",
                        backgroundColor: "#10304d",
                        borderRadius: "3px",
                        padding: "2px 6px",
                      }}
                    >
                      LATEST MASTER RESUME
                    </span>
                  )}
                </td>
                <td>{TYPE_LABELS[rv.type] || rv.type}</td>
                <td>{rv.fileName}</td>
                <td>{rv.atsScore != null ? rv.atsScore : "-"}</td>
                {user?.role === "ADMIN" && <td>{addedByLabel(rv.createdBy)}</td>}
                <td>{formatDateTime(rv.createdAt)}</td>
                <td>
                  <button type="button" onClick={() => handleDownload(rv.id, rv.fileName)}>
                    <DownloadIcon /> Download
                  </button>{" "}
                  {rv.type === "MASTER" && rv.parsedData && (
                    <span style={{ fontSize: "12px", color: "#2f7a3d", fontWeight: "bold" }}>
                      ✓ AI-Parsed
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialogElement}
    </div>
  );
}
